import type { Dispatch, SetStateAction } from 'react';
import { areObjectsEqual } from '../../../lib/objectUtils';
import { NoOp, VISIBILITY_PERCENTAGE_90 } from './Carousel.constants';
import type { CarouselItemsState, MobileScrollDotDetails } from './Carousel.types';

/**
 * Checks if scroll buttons should be visible based on device type and the number of items.
 * @returns {boolean} True if user is on mobiles, there is at least one carousel item and override flag is true, otherwise false.
 */
export const checkAreMobileScrollButtonsVisible = (params: {
  /** Indicates if the device is a desktop */
  isDesktop: boolean;
  /** The number of carousel items */
  itemsLength: number;
  /** Override flag */
  initialAreMobileScrollButtonsVisible: boolean;
}): boolean => {
  const { isDesktop, itemsLength, initialAreMobileScrollButtonsVisible } = params;
  return !isDesktop && itemsLength > 0 && initialAreMobileScrollButtonsVisible;
};

/**
 * Checks if scroll buttons should be visible based on device type and the number of items.
 * @returns {boolean} True if user is on desktops and there is at least one carousel item, otherwise false.
 */
export const checkAreDesktopScrollButtonsVisible = (params: {
  /** Indicates if the device is a desktop */
  isDesktop: boolean;
  /** The number of carousel items */
  itemsLength: number;
}): boolean => {
  const { isDesktop, itemsLength } = params;
  return isDesktop && itemsLength > 0;
};

/**
 * Determines the state of visible carousel items that contains the following:
 * - Index of the first fully visible carousel item
 * - Index of the last fully visible carousel item
 * @returns {CarouselItemsState} State of visible carousel items.
 */
export const getCarouselItemsState = (params: {
  /** Boolean array where true means that the carousel item at that index (0-based) is fully visible width-wise */
  carouselItemVisibilities: boolean[];
}): CarouselItemsState => {
  const { carouselItemVisibilities } = params;

  return {
    lowestCarouselVisibleItemIndex: carouselItemVisibilities.indexOf(true),
    highestCarouselVisibleItemIndex: carouselItemVisibilities.lastIndexOf(true),
  };
};

export const getMobileScrollDotDetails = (params: {
  areMobileScrollButtonsVisible: boolean;
  /** State of visible carousel items that contains the following:
   * - Index of the first fully visible carousel item
   * - Index of the last fully visible carousel item
   */
  carouselItemsState: CarouselItemsState;
  /** Boolean array where true means that the carousel item at that index (0-based) is fully visible width-wise */
  carouselItemVisibilities: boolean[];
}): MobileScrollDotDetails | undefined => {
  const { areMobileScrollButtonsVisible, carouselItemsState, carouselItemVisibilities } = params;

  if (areMobileScrollButtonsVisible && carouselItemsState.lowestCarouselVisibleItemIndex >= 0) {
    const totalDots: number = carouselItemVisibilities.length;

    // If the last carousel item is fully visible then set current dot index to that index
    const currentDotIndex: number = carouselItemsState.highestCarouselVisibleItemIndex === carouselItemVisibilities.length - 1
      ? carouselItemsState.highestCarouselVisibleItemIndex
      : carouselItemsState.lowestCarouselVisibleItemIndex;

    return { totalDots, currentDotIndex };
  }

  return undefined;
};

/**
 * Creates an intersection observer callback to keep track of what carousel items are visible on the screen and update the visibility array of carousel items.
 * @returns {IntersectionObserverCallback} Intersection observer callback.
 */
export const getCarouselIntersectionObserverCallback = (params: {
  /** Array of carousel item HTML elements */
  carouselItems: Element[];
  visibilityPercentage: number;
  /** Setter for carousel item visibilities */
  setCarouselItemVisibilities: Dispatch<SetStateAction<boolean[]>>;
}): IntersectionObserverCallback => {
  const { carouselItems, visibilityPercentage, setCarouselItemVisibilities } = params;

  return (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      const carouselItemIndex: number = carouselItems.indexOf(entry.target);

      if (carouselItemIndex >= 0) {
        const isCarouselItemVisible: boolean = entry.isIntersecting && entry.boundingClientRect.width * visibilityPercentage <= entry.intersectionRect.width;

        setCarouselItemVisibilities((prevCarouselVisibleItemIndexes: boolean[]) => {
          const newCarouselVisibleItemIndexes: boolean[] = [...prevCarouselVisibleItemIndexes];
          newCarouselVisibleItemIndexes[carouselItemIndex] = isCarouselItemVisible;

          return areObjectsEqual(prevCarouselVisibleItemIndexes, newCarouselVisibleItemIndexes)
            ? prevCarouselVisibleItemIndexes
            : newCarouselVisibleItemIndexes;
        });
      }
    }
  };
};

/**
 * Generates a function to scroll carousel to the left.
 * @returns {(() => void) | undefined} Function to scroll carousel to the left or undefined if not applicable.
 */
export const getOnGoBackwardClick = (params: {
  /** True if there is at least one carousel item and on mobiles override flag is true */
  areScrollButtonsVisible: boolean;
  /** State of visible carousel items that contains the following:
   * - Index of the first fully visible carousel item
   * - Index of the last fully visible carousel item
   */
  carouselItemsState: CarouselItemsState;
  /** HTML element of carousel items container */
  carouselItemsContainer: HTMLDivElement | null;
  /** Indicates if the device is a desktop */
  isDesktop: boolean;
}): (() => void) | undefined => {
  const { areScrollButtonsVisible, carouselItemsState, carouselItemsContainer, isDesktop } = params;

  if (areScrollButtonsVisible && carouselItemsContainer) {
    if (carouselItemsState.lowestCarouselVisibleItemIndex < 0) {
      return NoOp;
    }

    const carouselItems: HTMLCollection = carouselItemsContainer.children;

    // Proceed only if there are invisible carousel items on the left
    if (carouselItemsState.lowestCarouselVisibleItemIndex > 0) {
      // Get index of the last invisible carousel item on the left
      const prevCarouselInvisibleItemIndex: number = carouselItemsState.lowestCarouselVisibleItemIndex - 1;

      // Compute scroll distance
      // On desktops scroll by the width of all visible carousel items
      // On mobiles scroll so that the previous invisible carousel item becomes visible
      const scrollByPx: number = isDesktop
        ? carouselItems[carouselItemsState.highestCarouselVisibleItemIndex].getBoundingClientRect().right - carouselItems[prevCarouselInvisibleItemIndex].getBoundingClientRect().right
        : carouselItems[carouselItemsState.lowestCarouselVisibleItemIndex].getBoundingClientRect().right - carouselItems[prevCarouselInvisibleItemIndex].getBoundingClientRect().right;

      const scrollPx: number = carouselItemsContainer.scrollLeft - scrollByPx;

      return () => {
        // Using 'smooth' behavior to scroll with scroll transition
        carouselItemsContainer.scrollTo({ left: scrollPx, behavior: 'smooth' });

        // Fix for Chrome only:
        // In Chrome only, intersection observer callback is not always triggered on scroll with 'smooth' behavior (we use intersection observer to track visible items).
        // So we set a 400ms timeout to repeat the same scroll but this time we use 'auto' behavior (instant scroll without animation).
        // 400ms is enough time to complete scroll animation set by 'smooth' behavior.
        // So users should not see any difference but what's important is that 'auto' behavior triggers intersection observer callback in Chrome as expected.
        setTimeout(() => carouselItemsContainer.scrollTo({ left: scrollPx, behavior: 'auto' }), 400);
      };
    }
  }

  return undefined;
};

/**
 * Generates a function to scroll carousel to the right.
 * @returns {(() => void) | undefined} Function to scroll carousel to the right or undefined if not applicable.
 */
export const getOnGoForwardClick = (params: {
  /** True if there is at least one carousel item and on mobiles override flag is true */
  areScrollButtonsVisible: boolean;
  /** State of visible carousel items that contains the following:
   * - Index of the first fully visible carousel item
   * - Index of the last fully visible carousel item
   */
  carouselItemsState: CarouselItemsState;
  /** HTML element of carousel items container */
  carouselItemsContainer: HTMLDivElement | null;
  /** Indicates if the device is a desktop */
  isDesktop: boolean;
}): (() => void) | undefined => {
  const { areScrollButtonsVisible, carouselItemsState, carouselItemsContainer, isDesktop } = params;

  if (areScrollButtonsVisible && carouselItemsContainer) {
    if (carouselItemsState.lowestCarouselVisibleItemIndex < 0) {
      return NoOp;
    }

    const carouselItems: HTMLCollection = carouselItemsContainer.children;

    // Proceed only if there are carousel items on the right to scroll to
    if (carouselItemsState.highestCarouselVisibleItemIndex < carouselItems.length - 1) {
      // Get index of the first invisible carousel item on the right
      const nextCarouselInvisibleItemIndex: number = carouselItemsState.highestCarouselVisibleItemIndex + 1;

      // Compute scroll distance
      // On desktops scroll by the width of all visible carousel items
      // On mobiles scroll so that the next invisible carousel item becomes visible
      const scrollByPx: number = isDesktop
        ? carouselItems[nextCarouselInvisibleItemIndex].getBoundingClientRect().left - carouselItems[carouselItemsState.lowestCarouselVisibleItemIndex].getBoundingClientRect().left
        : carouselItems[nextCarouselInvisibleItemIndex].getBoundingClientRect().left - carouselItems[carouselItemsState.highestCarouselVisibleItemIndex].getBoundingClientRect().left;

      const scrollPx: number = carouselItemsContainer.scrollLeft + scrollByPx;

      return () => {
        // Using 'smooth' behavior to scroll with scroll transition
        carouselItemsContainer.scrollTo({ left: scrollPx, behavior: 'smooth' });

        // Fix for Chrome only:
        // In Chrome only, intersection observer callback is not always triggered on scroll with 'smooth' behavior (we use intersection observer to track visible items).
        // So we set a 400ms timeout to repeat the same scroll but this time we use 'auto' behavior (instant scroll without animation).
        // 400ms is enough time to complete scroll animation set by 'smooth' behavior.
        // So users should not see any difference but what's important is that 'auto' behavior triggers intersection observer callback in Chrome as expected.
        setTimeout(() => carouselItemsContainer.scrollTo({ left: scrollPx, behavior: 'auto' }), 400);
      };
    }
  }

  return undefined;
};

/**
 * Checks if we need to render additional left box shadow before the carousel to achieve the box shadow effect
 * @returns {boolean} True if areBoxShadowsEnabled is true and there is at least one carousel item.
 */
export const checkShouldRenderLeftBoxShadow = (params: {
  /**
   * Since carousel items are rendered within a container with "overflow: hidden" on desktops, all box shadows on carousel items get cut off.
   * This flag indicates if we need to render additional box shadows before and after the carousel to achieve the box shadow effect.
   */
  areBoxShadowsEnabled: boolean;
  /** The number of carousel items */
  itemsLength: number;
}): boolean => {
  const { areBoxShadowsEnabled, itemsLength } = params;
  return areBoxShadowsEnabled && itemsLength > 0;
};

/**
 * Checks if we need to render additional right box shadow after the carousel to achieve the box shadow effect
 * @returns {boolean} True if areBoxShadowsEnabled is true and there are enough carousel items to fill the entire screen width.
 */
export const checkShouldRenderRightBoxShadow = (params: {
  /**
   * Since carousel items are rendered within a container with "overflow: hidden" on desktops, all box shadows on carousel items get cut off.
   * This flag indicates if we need to render additional box shadows before and after the carousel to achieve the box shadow effect.
   */
  areBoxShadowsEnabled: boolean;
  /** Maximum available screen width in pixels for the carousel container */
  maxAvailableScreenWidthPx: number;
  /** HTML collection of carousel item HTML elements */
  carouselItems: HTMLCollection | undefined;
}): boolean => {
  const { areBoxShadowsEnabled, maxAvailableScreenWidthPx, carouselItems } = params;

  if (!areBoxShadowsEnabled || maxAvailableScreenWidthPx <= 0 || !carouselItems) {
    return false;
  }

  const carouselItemsTotalWidthPx: number = Array.from(carouselItems).reduce((totalWidthPx, carouselItem: Element) => {
    return totalWidthPx + carouselItem.getBoundingClientRect().width;
  }, 0);

  // Return true if the total width of carousel items covers at least 90% of available screen width
  // This accounts for gaps between carousel items
  return carouselItemsTotalWidthPx >= maxAvailableScreenWidthPx * VISIBILITY_PERCENTAGE_90;
};
